import { useState } from "react";
import { ShieldCheck, Trash2, UserPlus, EyeOff, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import {
  getAntinukeWhitelist,
  addAntinukeWhitelist,
  editAntinukeWhitelist,
  removeAntinukeWhitelist,
  getTrustedAdmins,
  addTrustedAdmin,
  removeTrustedAdmin,
  getExtraOwners,
  addExtraOwner,
  removeExtraOwner,
  getIgnoredChannels,
  addIgnoredChannel,
  removeIgnoredChannel,
} from "@/api/antinuke";
import { CrudCard, ListRow, IconButton } from "./CrudCard";
import { showApiError } from "./helpers";
import { TextInput, ChannelSelect, Toggle, selectCls } from "./fields";
import { MemberSelect, MemberTag } from "./MemberSelect";

const WL_PERMISSIONS = [
  "administrator",
  "channel_create",
  "channel_delete",
  "channel_update",
  "role_create",
  "role_delete",
  "role_update",
  "member_role_update",
  "ban",
  "kick",
  "bot_add",
  "webhook_create",
  "webhook_delete",
];

const isId = (v) => /^\d{5,25}$/.test(String(v).trim());

const IdList = ({ title, items, onAdd, onRemove, placeholder, testId, note, guildId, memberPicker }) => {
  const [value, setValue] = useState(memberPicker ? null : "");
  const [busy, setBusy] = useState(false);
  const validItems = items.filter(isId);
  const brokenCount = items.length - validItems.length;
  const submit = async () => {
    if (memberPicker ? !value : !isId(value)) {
      toast.error(memberPicker ? "Pick a member first." : "Enter a valid numeric Discord ID.");
      return;
    }
    setBusy(true);
    try {
      await onAdd(String(value).trim());
      setValue(memberPicker ? null : "");
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">{title}</p>
      <div className="mt-3 flex gap-2">
        <div className="min-w-0 flex-1">
          {memberPicker ? (
            <MemberSelect guildId={guildId} value={value} onChange={setValue} placeholder={placeholder} testId={`${testId}-input`} includeBots={false} />
          ) : (
            <TextInput value={value} onChange={setValue} placeholder={placeholder} testId={`${testId}-input`} />
          )}
        </div>
        <button
          onClick={submit}
          disabled={busy}
          data-testid={`${testId}-add`}
          className="shrink-0 self-start rounded-full bg-white px-4 py-2.5 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50"
        >
          Add
        </button>
      </div>
      {note && <p className="mt-2 text-xs text-paper-500/80">{note}</p>}
      {brokenCount > 0 && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-paper-400" data-testid={`${testId}-broken-warning`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {brokenCount} saved {brokenCount === 1 ? "entry" : "entries"} can't be shown — the API returned corrupted data. Apply the backend fix to restore them.
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {validItems.length === 0 && brokenCount === 0 && <span className="text-xs text-paper-500">None yet.</span>}
        {validItems.map((id) => (
          <span key={id} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 font-mono text-xs text-paper-200" data-testid={`${testId}-item-${id}`}>
            {memberPicker ? <MemberTag guildId={guildId} id={id} /> : id}
            <button onClick={() => onRemove(id).catch(showApiError)} aria-label={`Remove ${id}`} data-testid={`${testId}-remove-${id}`} className="text-paper-500 transition-colors hover:text-paper-100">
              <Trash2 className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

const WhitelistEditor = ({ guildId }) => {
  const { data, loading, error, refetch } = useApi(() => getAntinukeWhitelist(guildId));
  const [userId, setUserId] = useState(null);
  const [busy, setBusy] = useState(false);
  const entries = data?.whitelist || [];

  const add = async () => {
    if (!userId) {
      toast.error("Pick a member first.");
      return;
    }
    setBusy(true);
    try {
      await addAntinukeWhitelist(guildId, { user_id: String(userId) });
      toast.success("User whitelisted.");
      setUserId(null);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const togglePerm = async (entry, perm, value) => {
    try {
      await editAntinukeWhitelist(guildId, entry.user_id, { permissions: { [perm]: value } });
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const remove = async (entry) => {
    try {
      await removeAntinukeWhitelist(guildId, entry.user_id);
      toast.success("Removed from whitelist.");
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  if (loading) return <p className="text-sm text-paper-500">Loading whitelist…</p>;
  if (error) return <p className="text-sm text-paper-500">{error.message}</p>;

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="antinuke-whitelist">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">
        Whitelist ({entries.length})
      </p>
      <div className="mt-3 flex gap-2">
        <div className="min-w-0 flex-1">
          <MemberSelect guildId={guildId} value={userId} onChange={setUserId} placeholder="Search members to whitelist…" testId="antinuke-wl-input" includeBots={false} />
        </div>
        <button onClick={add} disabled={busy} data-testid="antinuke-wl-add" className="shrink-0 self-start rounded-full bg-white px-4 py-2.5 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50">
          Add
        </button>
      </div>
      <div className="mt-4 space-y-3">
        {entries.map((entry) => (
          <div key={entry.user_id} className="rounded-xl border border-white/5 px-4 py-3" data-testid={`antinuke-wl-entry-${entry.user_id}`}>
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-paper-200">
                <MemberTag guildId={guildId} id={entry.user_id} testId={`antinuke-wl-member-${entry.user_id}`} />
              </span>
              <IconButton onClick={() => remove(entry)} label="Remove from whitelist" danger testId={`antinuke-wl-remove-${entry.user_id}`}>
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </IconButton>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 min-[420px]:grid-cols-2 sm:grid-cols-3">
              {WL_PERMISSIONS.map((perm) => (
                <div key={perm} className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] text-paper-500">{perm}</span>
                  <Toggle
                    checked={!!entry[perm]}
                    onChange={(v) => togglePerm(entry, perm, v)}
                    label={`${perm} for ${entry.user_id}`}
                    testId={`antinuke-wl-${entry.user_id}-${perm}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const IgnoredChannels = ({ guildId, channels }) => {
  const { data, loading, error, refetch } = useApi(() => getIgnoredChannels(guildId));
  const [channelId, setChannelId] = useState("");
  const [busy, setBusy] = useState(false);
  const items = data?.ignored_channels || [];
  const name = (id) => {
    const ch = channels.find((c) => String(c.id) === String(id));
    return ch ? `#${ch.name}` : id;
  };
  const add = async () => {
    if (!channelId) return;
    setBusy(true);
    try {
      await addIgnoredChannel(guildId, channelId);
      toast.success("Channel ignored by anti-nuke.");
      setChannelId("");
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(false);
    }
  };
  if (loading) return <p className="text-sm text-paper-500">Loading ignored channels…</p>;
  if (error) return <p className="text-sm text-paper-500">{error.message}</p>;
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="antinuke-ignored">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">
        <EyeOff className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
        Ignored channels ({items.length})
      </p>
      <div className="mt-3 flex gap-2">
        <div className="flex-1">
          <ChannelSelect value={channelId} onChange={setChannelId} channels={channels} types={["text", "news", "voice", "stage_voice"]} testId="antinuke-ignored-select" />
        </div>
        <button onClick={add} disabled={busy || !channelId} data-testid="antinuke-ignored-add" className="shrink-0 rounded-full bg-white px-4 py-2 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50">
          Add
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.map((id) => (
          <span key={id} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 font-mono text-xs text-paper-200" data-testid={`antinuke-ignored-item-${id}`}>
            {name(id)}
            <button onClick={() => removeIgnoredChannel(guildId, id).then(refetch).catch(showApiError)} aria-label={`Remove ${name(id)}`} data-testid={`antinuke-ignored-remove-${id}`} className="text-paper-500 transition-colors hover:text-paper-100">
              <Trash2 className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export const AntinukeLists = ({ guildId, channels }) => {
  const admins = useApi(() => getTrustedAdmins(guildId));
  const owners = useApi(() => getExtraOwners(guildId));

  return (
    <div className="space-y-4" data-testid="antinuke-lists">
      <WhitelistEditor guildId={guildId} />
      <div className="grid gap-4 lg:grid-cols-2">
        <IdList
          title={<span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />Trusted admins ({admins.data?.trusted_admins?.length || 0})</span>}
          items={admins.data?.trusted_admins || []}
          onAdd={async (id) => { await addTrustedAdmin(guildId, id); toast.success("Trusted admin added."); await admins.refetch(); }}
          onRemove={async (id) => { await removeTrustedAdmin(guildId, id); toast.success("Trusted admin removed."); await admins.refetch(); }}
          placeholder="Search members…"
          testId="antinuke-trusted"
          note="Trusted admins bypass anti-nuke punishments."
          guildId={guildId}
          memberPicker
        />
        <IdList
          title={<span className="inline-flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" aria-hidden="true" />Extra owners ({owners.data?.extra_owners?.length || 0})</span>}
          items={owners.data?.extra_owners || []}
          onAdd={async (id) => { await addExtraOwner(guildId, id); toast.success("Extra owner added."); await owners.refetch(); }}
          onRemove={async (id) => { await removeExtraOwner(guildId, id); toast.success("Extra owner removed."); await owners.refetch(); }}
          placeholder="Search members…"
          testId="antinuke-owners"
          note="Only the server owner can manage extra owners."
          guildId={guildId}
          memberPicker
        />
      </div>
      <IgnoredChannels guildId={guildId} channels={channels} />
    </div>
  );
};

export const AntinukeListsIcon = UserPlus;
